import axios from "axios";
// eslint-disable-next-line import/no-cycle
import { getCurrentIdToken, isSessionValid, isSigningOut, signOut } from "pages/Auth/Cogcognito";
import { showError } from "utils/notifications";
import { diagnosticEndpoint, newDiagnosticId, recordDiagnostic, reportError } from "utils/diagnostics";

const axiosInst = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

axiosInst.interceptors.request.use(async (config) => {
  const requestConfig = { ...config };
  requestConfig.diagnostics = {
    request_id: newDiagnosticId(),
    method: (config.method || "get").toUpperCase(),
    endpoint: diagnosticEndpoint(config.url),
  };
  requestConfig.diagnosticStartedAt = Date.now();
  recordDiagnostic("api.started", requestConfig.diagnostics);
  requestConfig.headers = { ...config.headers, Authorization: await getCurrentIdToken() };
  return requestConfig;
});

const responseDiagnostics = (response, config) => ({
  ...config?.diagnostics,
  duration_ms: config?.diagnosticStartedAt ? Date.now() - config.diagnosticStartedAt : undefined,
  status: response?.status,
  gateway_request_id: response?.headers?.["x-amzn-requestid"],
  // The cloud wrapper exposes its Lambda request ID without a new request header/preflight requirement.
  server_request_id: response?.headers?.["x-megaportal-request-id"],
});

axiosInst.interceptors.response.use(
  async (response) => {
    recordDiagnostic("api.completed", responseDiagnostics(response, response.config));
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      recordDiagnostic("api.cancelled", responseDiagnostics(null, error.config));
      return Promise.reject(error);
    }
    reportError("api.request", error, responseDiagnostics(error.response, error.config));

    // axios 0.27.x has a quirk: on network errors it sets error.response to the XHR
    // object (same value as error.request). Detect via error.code so we don't treat
    // that XHR as a real response.
    const isNetworkError =
      !error.response ||
      error.code === "ERR_NETWORK" ||
      error.code === "ECONNABORTED" ||
      error.code === "ETIMEDOUT" ||
      error.response === error.request;

    const status = isNetworkError ? undefined : error.response?.status;
    const data = isNetworkError ? undefined : error.response?.data;

    if (isSigningOut()) {
      // A failed VPN cleanup must not recursively start (or wait for) the same logout.
    } else if (!isSessionValid()) {
      showError("Invalid session please sign in again!");
      await signOut();
      window.location.href = "/login";
    } else if (status === 401 || data?.message === "Unauthorized") {
      showError("Unauthorized!");
      await signOut();
      window.location.href = "/login";
    } else if (isNetworkError) {
      showError(error.message || "Network error");
    } else {
      const { error: errorMsg, message } = data || {};
      showError(errorMsg || message);
    }

    try {
      localStorage.setItem("apiFailCount", String((Number(localStorage.getItem("apiFailCount")) || 0) + 1));
    } catch {
      recordDiagnostic("storage.unavailable", { operation: "api.failure_count" }, "warn");
    }

    // Reject with the response on real HTTP errors, the AxiosError on network errors
    // (never the raw XHR - that's what causes [object XMLHttpRequest] in the React error overlay).
    return Promise.reject(isNetworkError ? error : error.response);
  },
);

export default axiosInst;
